<template>
  <div class="modern-login-page">
    <div class="login-container">
      <div class="login-wrapper">
        <!-- Left Side - Branding -->
        <div class="branding-section">
          <div class="branding-content">
            <div class="brand-header">
              <div class="logo-container">
                <img src="@/assets/images/logo.png" alt="Ovvura" class="brand-logo" />
              </div>
              <h1 class="brand-title">Welcome to Ovvura</h1>
              <p class="brand-subtitle">Your trusted platform for seamless service bookings and business management.</p>
            </div>

            <div class="features-showcase">
              <div class="showcase-item">
                <div class="showcase-icon">
                  <i class="mdi mdi-lightning-bolt"></i>
                </div>
                <div class="showcase-text">
                  <h4>Fast & Efficient</h4>
                  <p>Access your dashboard instantly with secure authentication</p>
                </div>
              </div>

              <div class="showcase-item">
                <div class="showcase-icon">
                  <i class="mdi mdi-account-group"></i>
                </div>
                <div class="showcase-text">
                  <h4>Connect & Grow</h4>
                  <p>Join thousands of service providers and customers</p>
                </div>
              </div>

              <div class="showcase-item">
                <div class="showcase-icon">
                  <i class="mdi mdi-chart-line"></i>
                </div>
                <div class="showcase-text">
                  <h4>Track Progress</h4>
                  <p>Monitor your bookings and business growth in real-time</p>
                </div>
              </div>
            </div>

          </div>
        </div>

        <!-- Right Side - Login Form -->
        <div class="form-section">
          <div class="form-content">
            <div class="form-header">
              <div class="mobile-logo">
                <img src="@/assets/images/logo.png" alt="Ovvura" class="logo-mobile" />
              </div>
              <p class="form-subtitle">Sign in to continue to your account</p>
            </div>

            <!-- Error Alert -->
            <transition name="fade">
              <div v-if="error" class="alert alert-danger">
                <i class="mdi mdi-alert-circle"></i>
                <span>{{ error }}</span>
                <button @click="error = ''" class="alert-close">
                  <i class="mdi mdi-close"></i>
                </button>
              </div>
            </transition>

            <!-- Login Form -->
            <b-form class="login-form" @submit.prevent="login">
              <div class="form-group">
                <label class="form-label">
                  <i class="mdi mdi-email-outline"></i>
                  Email Address
                </label>
                <div class="input-wrapper">
                  <span class="input-icon">
                    <i class="mdi mdi-account-outline"></i>
                  </span>
                  <input
                    id="email-input"
                    v-model="email"
                    type="email"
                    class="form-control"
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>

              <div class="form-group">
                <label class="form-label">
                  <i class="mdi mdi-lock-outline"></i>
                  Password
                </label>
                <div class="input-wrapper password-wrapper">
                  <span class="input-icon">
                    <i class="mdi mdi-lock-outline"></i>
                  </span>
                  <input
                    id="password-input"
                    v-model="password"
                    :type="inputType"
                    class="form-control"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    class="password-toggle"
                    @click="togglePasswordVisibilty"
                    :title="showPassword ? 'Hide password' : 'Show password'"
                  >
                    <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                  </button>
                </div>
              </div>

              <div class="form-options">
                <div class="remember-me">
                  <input
                    type="checkbox"
                    id="remember-check"
                    class="custom-checkbox"
                  />
                  <label for="remember-check">Remember me</label>
                </div>
                <a href="#" class="forgot-link">Forgot password?</a>
              </div>

              <div class="form-actions">
                <b-button
                  type="submit"
                  class="login-btn"
                  :disabled="loader"
                >
                  <span v-if="!loader">
                    <i class="mdi mdi-login"></i>
                    Sign In
                  </span>
                  <span v-else class="loading-content">
                    <b-spinner small class="btn-spinner" />
                    Signing in...
                  </span>
                </b-button>
              </div>

              <div class="form-footer">
                <p class="signup-text">
                  Don't have an account?
                  <router-link to="/register" class="signup-link">
                    Sign up now
                    <i class="mdi mdi-arrow-right"></i>
                  </router-link>
                </p>
              </div>
            </b-form>

          </div>
        </div>
      </div>
    </div>

    <!-- Decorative Elements -->
    <div class="decorative-circles">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import appConfig from "@/app.config";

export default {
  page: {
    title: "Login",
    meta: [{ name: "description", content: appConfig.description }],
  },
  data() {
    return {
      email: "",
      password: "",
      error: "",
      loader: false,
      showPassword: false,
    };
  },
  methods: {
    async login() {
      this.loader = true;
      this.error = "";
      
      let formData = new FormData();
      formData.append("email", this.email);
      formData.append("password", this.password);
      let url = "/login";
      
      try {
        const response = await axios.post(url, formData);
        if (response.status == 200) {
          let userData = response.data.user;
          let token = response.data.token;
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          localStorage.setItem("user", JSON.stringify(userData));
          localStorage.setItem("token", token);
          this.$router.push(
            this.$route.query.redirectFrom || { name: "dashboard" }
          );
        } else {
          this.error = response.data.message;
        }
      } catch (err) {
        this.error = err.response?.data?.message || "Login failed. Please check your credentials.";
      } finally {
        this.loader = false;
      }
    },
    togglePasswordVisibilty() {
      this.showPassword = !this.showPassword;
    },
  },
  mounted() {
    document.body.classList.add("auth-body-bg");
  },
  computed: {
    inputType() {
      return this.showPassword ? "text" : "password";
    },
  },
};
</script>

<style scoped>
/* Modern Login Page Styles */
.modern-login-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #E6FDFF 0%, #F5F7F7FF 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  overflow: hidden;
}

/* Decorative Elements */
.decorative-circles {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.circle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(0, 0, 212, 0.1) 0%, rgba(0, 14, 168, 0.05) 100%);
}

.circle-1 {
  width: 400px;
  height: 400px;
  top: -200px;
  right: -100px;
  animation: float 20s infinite ease-in-out;
}

.circle-2 {
  width: 300px;
  height: 300px;
  bottom: -150px;
  left: -50px;
  animation: float 15s infinite ease-in-out reverse;
}

.circle-3 {
  width: 200px;
  height: 200px;
  top: 50%;
  right: 10%;
  animation: float 25s infinite ease-in-out;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-30px) rotate(180deg);
  }
}

/* Login Container */
.login-container {
  max-width: 1000px;
  width: 100%;
  position: relative;
  z-index: 1;
}

.login-wrapper {
  display: grid;
  grid-template-columns: 480px 1fr;
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.12);
  overflow: hidden;
  /* min-height: 650px; */
}

@media (max-width: 1024px) {
  .login-wrapper {
    grid-template-columns: 1fr;
    max-width: 500px;
    margin: 0 auto;
  }
}

/* Branding Section */
.branding-section {
  background: linear-gradient(135deg, #3A2B78FF 0%, #3A2B78FF 100%);
  padding: 3rem 2rem;
  color: white;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.branding-section::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: pulse 4s infinite ease-in-out;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.8;
  }
}

@media (max-width: 1024px) {
  .branding-section {
    display: none;
  }
}

.branding-content {
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  z-index: 1;
}

.brand-header {
  margin-bottom: 1rem;
}

.logo-container {
  margin-bottom: 1.5rem;
}

.brand-logo {
  height: 60px;
  width: auto;
  filter: brightness(0) invert(1);
}

.brand-title {
  font-size: 1.5rem;
  font-weight: 500;
  margin-bottom: 0.75rem;
  line-height: 1.2;
  color: #FFFFFFFF;
}

.brand-subtitle {
  font-size: 1rem;
  opacity: 0.8;
  line-height: 1.6;
}

/* Features Showcase */
.features-showcase {
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  margin-bottom: 3rem;
  flex: 1;
}

.showcase-item {
  display: flex;
  gap: 1rem;
  align-items: flex-start;
}

.showcase-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  flex-shrink: 0;
  backdrop-filter: blur(10px);
}

.showcase-text h4 {
  font-size: 1.0rem;
  font-weight: 600;
  margin: 0 0 0.25rem 0;
  color: #FFFFFFFF;
}

.showcase-text p {
  font-size: 0.8rem;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
}

/* Form Section */
.form-section {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 2.5rem;
}

@media (max-width: 768px) {
  .form-section {
    padding: 2rem 1.5rem;
  }
}

.form-content {
  width: 100%;
  max-width: 420px;
}

.form-header {
  text-align: center;
  margin-bottom: 2rem;
}

.mobile-logo {
  display: none;
  margin-bottom: 1.5rem;
}

@media (max-width: 1024px) {
  .mobile-logo {
    display: block;
  }
}

.logo-mobile {
  height: 50px;
  width: auto;
}

.form-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.form-subtitle {
  font-size: 1rem;
  color: #101010FF;
  margin: 0;
  font-family: Arial, Helvetica, sans-serif;
}

/* Alert */
.alert {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
  background: #f8d7da;
  color: #721c24;
  border: none;
  position: relative;
}

.alert i:first-child {
  font-size: 1.25rem;
  flex-shrink: 0;
}

.alert span {
  flex: 1;
  font-size: 0.9rem;
}

.alert-close {
  background: none;
  border: none;
  color: #721c24;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.3s ease;
}

.alert-close:hover {
  background: rgba(0, 0, 0, 0.1);
}

.fade-enter-active, .fade-leave-active {
  transition: all 0.3s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Login Form */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-weight: 500;
  color: #495057;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.form-label i {
  font-size: 1.1rem;
  color: #3A2B78FF;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.input-icon {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3A2B78FF;
  font-size: 1.2rem;
  z-index: 2;
}

.form-control {
  flex: 1;
  padding: 0.875rem 1rem 0.875rem 3rem !important;
  border: 2px solid #e1e8ed !important;
  border-radius: 12px !important;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  background: white;
}

.form-control:focus {
  border-color: #3A2B78FF !important;
  box-shadow: 0 0 0 0.25rem rgba(0, 168, 168, 0.1) !important;
  outline: none;
}

.password-wrapper {
  position: relative;
}

.password-toggle {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: all 0.3s ease;
  z-index: 3;
}

.password-toggle:hover {
  background: #f1f3f5;
  color: #3A2B78FF;
}

.password-toggle i {
  font-size: 1.2rem;
}

/* Form Options */
.form-options {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: -0.5rem;
}

.remember-me {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.custom-checkbox {
  width: 18px;
  height: 18px;
  border: 2px solid #3A2B78FF;
  border-radius: 4px;
  cursor: pointer;
  accent-color: #3A2B78FF;
}

.remember-me label {
  font-size: 0.9rem;
  color: #495057;
  cursor: pointer;
  margin: 0;
}

.forgot-link {
  font-size: 0.9rem;
  color: #3A2B78FF;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.3s ease;
}

.forgot-link:hover {
  color: #3A2B78FF;
  text-decoration: underline;
}

/* Login Button */
.login-btn {
  width: 100%;
  padding: 1rem 1.2rem !important;
  background: linear-gradient(135deg, #3A2B78FF 0%, #3A2B78FF 100%) !important;
  border: none !important;
  border-radius: 12px !important;
  color: white !important;
  font-size: 1.05rem !important;
  font-weight: 600 !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 0.75rem !important;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 12px rgba(8, 0, 168, 0.3) !important;
}

.login-btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 67, 168, 0.4) !important;
}

.login-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.login-btn i {
  font-size: 1.2rem;
}

.loading-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.btn-spinner {
  width: 1.2rem;
  height: 1.2rem;
}

/* Divider */
.divider {
  position: relative;
  text-align: center;
  margin: 1.0rem 0;
}

.divider::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 100%;
  height: 1px;
  background: #e1e8ed;
}

.divider span {
  position: relative;
  background: white;
  padding: 0 1rem;
  color: #6c757d;
  font-size: 0.9rem;
}


/* Form Footer */
.form-footer {
  text-align: center;
  padding-top: 1.5rem;
}

.signup-text {
  font-size: 0.95rem;
  color: #6c757d;
  margin: 0;
}

.signup-link {
  color: #3A2B78FF;
  text-decoration: none;
  font-weight: 600;
  margin-left: 0.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  transition: all 0.3s ease;
}

.signup-link:hover {
  color: #3A2B78FF;
  gap: 0.5rem;
}

.signup-link i {
  font-size: 1rem;
}


/* Responsive Design */
@media (max-width: 768px) {
  .modern-login-page {
    padding: 1rem;
  }

  .login-wrapper {
    border-radius: 16px;
    min-height: auto;
  }

  .form-title {
    font-size: 1.6rem;
  }

  .form-section {
    padding: 2rem 1.5rem;
  }

  .social-login {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .form-title {
    font-size: 1.4rem;
  }

  .form-options {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .login-btn {
    padding: 0.875rem 1.5rem !important;
  }
}

/* Accessibility */
*:focus-visible {
  outline: 3px solid #3A2B78FF;
  outline-offset: 2px;
}

/* Reduced Motion */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}
</style>